import { Box, Text, VStack } from "@chakra-ui/react";
import { forwardRef } from "react";
import QRCode from "qrcode.react";

const CARDS_PER_ROW = 2;
const ROWS_PER_PAGE = 4;
const CARDS_PER_PAGE = CARDS_PER_ROW * ROWS_PER_PAGE;

const getQrValue = (studentId) =>
  `https://enoughphysics.com/scan-code/${studentId}/attendence`;

const chunkStudents = (students, size) => {
  const pages = [];
  for (let i = 0; i < students.length; i += size) {
    pages.push(students.slice(i, i + size));
  }
  return pages;
};

const StudentCard = ({ student }) => (
  <Box
    className="student-card"
    border="1px solid"
    borderColor="main_1"
    borderRadius="10px"
    p={3}
    bg="white"
    textAlign="center"
    display="flex"
    flexDirection="column"
    justifyContent="space-between"
    h="100%"
    w="100%"
    minH={0}
  >
    <VStack spacing={1} className="student-card-details" flexShrink={0}>
      <Text className="card-name" fontWeight="700" fontSize="sm" color="main_1" noOfLines={2}>
        {student.name}
      </Text>
      <Text className="card-detail" fontSize="xs" noOfLines={1}>
        <Text as="span" fontWeight="600">
          Username:
        </Text>{" "}
        {student.user_name}
      </Text>
      <Text className="card-detail" fontSize="xs" noOfLines={1}>
        <Text as="span" fontWeight="600">
          Group:
        </Text>{" "}
        {student.group?.name}
      </Text>
      <Text className="card-detail" fontSize="xs" noOfLines={1}>
        <Text as="span" fontWeight="600">
          Phone:
        </Text>{" "}
        {student.phone}
      </Text>
      <Text className="card-detail" fontSize="xs" noOfLines={1}>
        <Text as="span" fontWeight="600">
          Mother Phone:
        </Text>{" "}
        {student.mother_phone}
      </Text>
    </VStack>

    <Box className="card-qr" display="flex" justifyContent="center" alignItems="center" flex={1} minH={0}>
      <QRCode value={getQrValue(student.id)} size={96} />
    </Box>
  </Box>
);

const StudentPrintCards = forwardRef(({ students, courseName }, ref) => {
  const pages = chunkStudents(students, CARDS_PER_PAGE);

  return (
    <Box ref={ref} className="print-cards-wrapper" p={4} bg="white">
      <style>
        {`
          .print-page {
            display: grid;
            grid-template-columns: repeat(${CARDS_PER_ROW}, 1fr);
            grid-template-rows: repeat(${ROWS_PER_PAGE}, 1fr);
            gap: 12px;
            width: 100%;
            max-width: 720px;
            min-height: 900px;
            margin: 0 auto 24px;
          }

          @media print {
            @page {
              size: A4 portrait;
              margin: 5mm;
            }

            html,
            body {
              width: 100%;
              height: 100%;
              margin: 0 !important;
              padding: 0 !important;
            }

            .print-cards-wrapper {
              padding: 0 !important;
              margin: 0 !important;
              width: 100% !important;
              height: 100% !important;
            }

            .print-course-title {
              display: none;
            }

            .print-page {
              display: grid;
              grid-template-columns: repeat(${CARDS_PER_ROW}, 1fr);
              grid-template-rows: repeat(${ROWS_PER_PAGE}, 1fr);
              gap: 2.5mm;
              width: 100%;
              height: 100vh;
              max-width: none;
              min-height: 0;
              margin: 0;
              padding: 0;
              page-break-after: always;
              break-after: page;
              align-content: stretch;
              align-items: stretch;
            }

            .print-page:last-child {
              page-break-after: auto;
              break-after: auto;
            }

            .student-card {
              break-inside: avoid;
              page-break-inside: avoid;
              display: flex !important;
              flex-direction: column !important;
              justify-content: space-between !important;
              width: 100% !important;
              height: 100% !important;
              min-height: 0 !important;
              max-width: none !important;
              margin: 0 !important;
              padding: 2.5mm !important;
              border-radius: 4px !important;
              box-sizing: border-box !important;
            }

            .student-card-details {
              gap: 0.8mm !important;
            }

            .card-name {
              font-size: 9pt !important;
              line-height: 1.2 !important;
            }

            .card-detail {
              font-size: 7pt !important;
              line-height: 1.2 !important;
            }

            .card-qr {
              flex: 1 !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              min-height: 0 !important;
              margin-top: 1mm !important;
            }

            .card-qr svg,
            .card-qr canvas {
              width: auto !important;
              height: 100% !important;
              max-width: 30mm !important;
              max-height: 30mm !important;
            }
          }
        `}
      </style>

      {courseName && (
        <Text
          fontSize="lg"
          fontWeight="700"
          color="main_1"
          textAlign="center"
          mb={4}
          className="print-course-title"
        >
          {courseName}
        </Text>
      )}

      {pages.map((pageStudents, pageIndex) => (
        <Box key={`page-${pageIndex}`} className="print-page">
          {pageStudents.map((student) => (
            <StudentCard key={student.id} student={student} />
          ))}
        </Box>
      ))}
    </Box>
  );
});

StudentPrintCards.displayName = "StudentPrintCards";

export default StudentPrintCards;
